import * as React from 'react';
import {
  Button,
  ContentWell,
  Form,
  Heading,
  Input,
  Tabs
} from '@built/design-system';
import l10n from '@built/i18n';
import * as QRCode from 'qrcode.react';

import IntlTelInput from 'react-intl-tel-input';
import 'react-intl-tel-input/dist/main.css';

import { AuthError } from '../types';
import ActionButton from '../containers/ActionButton';
import Notification from '../containers/Notification';
import * as styles from './SetupMfa.module.scss';

/**
 * Renders UI to setup MFA
 */
export default class SetupMfa extends React.Component<SetupMfaProps> {
  render() {
    const { mfaComplete } = this.props;

    return (
      <div className={styles.verticalSpacing}>
        <Heading size="xxl">
          {l10n.t('SetupMfa:Heading.title', 'Enable Two-Factor Authentication')}
        </Heading>

        <p className={styles.lead}>
          {l10n.t(
            'SetupMfa:Heading.lead',
            `Built has an added layer of security to protect your personal privacy
            and loan information. Select an option below to enable.`
          )}
        </p>

        <Tabs
          onTabChange={this.handleTabChange}
          disabled={mfaComplete}
          className={styles.tabs}
        >
          <Tabs.Tab
            label={l10n.t('SetupMfa:SmsTab.tabLabel', 'SMS Text Message')}
            className={[styles.verticalSpacing].join(' ')}
          >
            {this.renderSmsTab()}
          </Tabs.Tab>

          <Tabs.Tab
            label={l10n.t('SetupMfa:TotpTab.tabLabel', 'Authenticator App')}
            className={[styles.verticalSpacing].join(' ')}
          >
            {this.renderTotpTab()}
          </Tabs.Tab>
        </Tabs>

        {mfaComplete && (
          <ActionButton
            label={l10n.t('SetupMfa:ActionButton.continue', 'Continue')}
            loading={this.props.busy}
            onClick={this.props.onComplete}
          />
        )}
      </div>
    );
  }

  handleTabChange = (label: string) => {
    const type = /SMS/.test(label) ? 'sms' : 'totp';
    this.props.onTypeChange(type);
  };

  renderSmsTabComplete = () => {
    const { phoneNumber } = this.props;
    const phoneSlice = phoneNumber.slice(phoneNumber.length - 4);
    return (
      <Notification
        type="success"
        title={l10n.t(
          'SetupMfa:Common.successTitle',
          'Two-Factor Authentication has been enabled.'
        )}
      >
        <p>
          {l10n.t('SetupMfa:SmsTab.successMessage1', 'The number ending in')}{' '}
          <b>{phoneSlice}</b>{' '}
          {l10n.t(
            'SetupMfa:SmsTab.successMessage2',
            'will be used to verify your identity when you log in.'
          )}
        </p>
      </Notification>
    );
  };

  renderSmsTabCollectPhoneNumber = () => {
    const {
      busy,
      codeOrPhone,
      onPhoneChange,
      phoneNumberValid,
      showPhoneError
    } = this.props;
    const formGroupStyles = [styles.mobileFormGroup];
    const inputStyles = [];

    let formGroupSubtext = l10n.t(
      'SetupMfa:SmsTab.messagingRatesWarning',
      'Standard messaging rates may apply.'
    );
    if (showPhoneError && !phoneNumberValid) {
      formGroupStyles.push(styles.error);
      formGroupSubtext = l10n.t(
        'SetupMfa:SmsTab.errors.invalidPhoneNumber',
        'Please provide a valid phone number.'
      );

      // NOTE: not using module/scoped class
      inputStyles.push('error');
    }

    return (
      <ContentWell className={styles.allowOverflow}>
        <form onSubmit={this.props.onSubmitPhoneNumber}>
          <Form.Group
            label={l10n.t(
              'SetupMfa:SmsTab.enterPhoneNumberFormGroupLabel',
              'Phone number'
            )}
            subtext={formGroupSubtext}
            className={formGroupStyles.join(' ')}
          >
            <IntlTelInput
              autoComplete="phone"
              autoFocus={true}
              disabled={busy}
              fieldName="code"
              format={true}
              inputClassName={inputStyles.join(' ')}
              onPhoneNumberChange={onPhoneChange}
              placeholder=""
              preferredCountries={['us', 'ca']}
              value={codeOrPhone}
            />
            <Button
              type="submit"
              loading={busy}
              label={l10n.t(
                'SetupMfa:SmsTab.sendVerificationCodeButtonLabel',
                'Send Verification Code'
              )}
              className={styles.btnSendCode}
            />
          </Form.Group>
        </form>
      </ContentWell>
    );
  };

  renderSmsTabCollectCode = () => {
    const {
      authError,
      busy,
      codeOrPhone,
      onCodeChange,
      phoneNumber,
      onSubmitSmsCode
    } = this.props;
    const phoneSlice = phoneNumber.slice(phoneNumber.length - 4);

    return (
      <ContentWell className={styles.allowOverflow}>
        <form onSubmit={onSubmitSmsCode}>
          <Form.Group
            label={l10n.t(
              'SetupMfa:Common.verificationCodeFormGroupLabel',
              'Verification code'
            )}
            subtext={
              <React.Fragment>
                {l10n.t(
                  'SetupMfa:SmsTab.enterSmsVerificationCodeMessage1',
                  'Enter the code we just sent to the number ending in'
                )}{' '}
                <b>{phoneSlice}</b>{' '}
                {l10n.t(
                  'SetupMfa:SmsTab.enterSmsVerificationCodeMessage2',
                  "via SMS. Didn't receive a code?"
                )}
                <Button
                  buttonType="link"
                  onClick={this.props.onResendSmsCode}
                  label={l10n.t(
                    'SetupMfa:SmsTab.requestNewVerificationCodeButtonLabel',
                    'Request a new code'
                  )}
                />
                .
              </React.Fragment>
            }
            className={styles.mobileFormGroup}
          >
            <Input
              autoFocus={true}
              className={authError ? styles.error : undefined}
              disabled={busy}
              label=""
              name="code"
              value={codeOrPhone}
              onChange={onCodeChange}
              subtext={authError ? authError.message : undefined}
            />

            <Button
              type="submit"
              loading={busy}
              label={l10n.t(
                'SetupMfa:Common.confirmVerificationCodeButtonLabel',
                'Confirm Code'
              )}
              className={styles.btnSendCode}
            />
          </Form.Group>
        </form>
      </ContentWell>
    );
  };

  renderSmsTab = () => {
    const { mfaComplete, phoneNumber } = this.props;

    return (
      <React.Fragment>
        <Heading size="lg">
          {l10n.t('SetupMfa:SmsTab.heading', 'Enabling via SMS Text Message')}
        </Heading>
        <p>
          {l10n.t(
            'SetupMfa:SmsTab.lead',
            `Enter a phone number that can receive SMS messages. The phone number
            you provide will be used to verify your identity when you log in.`
          )}
        </p>

        {mfaComplete && this.renderSmsTabComplete()}
        {!mfaComplete && !phoneNumber && this.renderSmsTabCollectPhoneNumber()}
        {!mfaComplete && phoneNumber && this.renderSmsTabCollectCode()}
      </React.Fragment>
    );
  };

  renderTotpTabInstructions = () => {
    return (
      <div className={styles.authInstructions}>
        <ol className={styles.numbers}>
          <li>
            {l10n.t(
              'SetupMfa:TotpTab.instructions1',
              'Download a two-factor authenticator app like'
            )}{' '}
            <a
              href="https://support.google.com/accounts/answer/1066447?co=GENIE.Platform%3DiOS&hl=en&oco=1"
              target="_blank"
            >
              Google Authenticator
            </a>
            ,{' '}
            <a href="https://authy.com/download/" target="_blank">
              Authy
            </a>
            , or{' '}
            <a
              href="https://duo.com/product/trusted-users/two-factor-authentication/duo-mobile"
              target="_blank"
            >
              Duo Mobile
            </a>
            .
          </li>
          <li>
            {l10n.t(
              'SetupMfa:TotpTab.instructions2',
              'Scan the QR Code on the right into your authenticator app.'
            )}
          </li>
          <li>
            {l10n.t(
              'SetupMfa:TotpTab.instructions3',
              `After scanning in the QR code, enter the code displayed in your
              authenticator app below.`
            )}
          </li>
        </ol>
        <QRCode
          size={200}
          value={
            this.props.username && this.props.mfaSecret
              ? `otpauth://totp/Built:${encodeURIComponent(
                  this.props.username
                )}?secret=${this.props.mfaSecret}`
              : ''
          }
        />
      </div>
    );
  };

  renderTotpTabComplete = () => {
    return (
      <Notification
        type="success"
        title={l10n.t(
          'SetupMfa:Common.successTitle',
          'Two-Factor Authentication has been enabled.'
        )}
      >
        <p>
          {l10n.t(
            'SetupMfa:TotpTab.successMessage',
            `Your authenticator app has been set up and will be used to verify your
            identity when you log in.`
          )}
        </p>
      </Notification>
    );
  };

  renderTotpTabCollectCode = () => {
    const { authError, busy, codeOrPhone } = this.props;

    return (
      <ContentWell>
        <form onSubmit={this.props.onSubmitTotpCode}>
          <Form.Group
            label={l10n.t(
              'SetupMfa:Common.verificationCodeFormGroupLabel',
              'Verification code'
            )}
            subtext={l10n.t(
              'SetupMfa:TotpTab.enterTotpVerificationCodeMessage',
              'Enter the code displayed in your authenticator app.'
            )}
            className={styles.mobileFormGroup}
          >
            <Input
              autoFocus={true}
              className={authError ? styles.error : undefined}
              disabled={busy}
              label=""
              name="code"
              value={codeOrPhone}
              onChange={this.props.onCodeChange}
              subtext={authError ? authError.message : undefined}
            />
            <Button
              type="submit"
              label={l10n.t(
                'SetupMfa:Common.confirmVerificationCodeButtonLabel',
                'Confirm Code'
              )}
              loading={busy}
            />
          </Form.Group>
        </form>
      </ContentWell>
    );
  };

  renderTotpTab = () => {
    const { mfaComplete } = this.props;

    return (
      <React.Fragment>
        <Heading size="lg">
          {l10n.t('SetupMfa:TotpTab.heading', 'Enabling via Authenticator App')}
        </Heading>
        {this.renderTotpTabInstructions()}

        {mfaComplete && this.renderTotpTabComplete()}
        {!mfaComplete && this.renderTotpTabCollectCode()}
      </React.Fragment>
    );
  };
}

export interface SetupMfaProps {
  /** An error created during the setup process */
  authError?: AuthError;

  /** True if an operation is in progress */
  busy?: boolean;

  /** Whether MFA has been completed (or not required) for the user */
  mfaComplete?: boolean;

  /** The TOTP MFA secret */
  mfaSecret?: string;

  /** The phone number or code entered by the user */
  codeOrPhone: string;

  /** The type of MFA to use */
  type: string;

  /** The phone number to use for SMS MFA */
  phoneNumber: string;

  /** Whether the provided phone number is valid or not */
  phoneNumberValid?: boolean;

  /** Whether the phone number has been verified */
  phoneNumberVerified: boolean;

  /** Whether to show phone number errors or not */
  showPhoneError: boolean;

  /** The currently logged in user's username */
  username: string;

  /** Event handler for when the type of MFA is changed */
  onTypeChange(type: MfaType): void;

  /** Event handler for when a code input field is changed */
  onCodeChange(event: React.SyntheticEvent<HTMLInputElement>): void;

  /** Event handler for when phone input field is changed */
  onPhoneChange(
    isValid: boolean,
    newNumber: string,
    countryData: {},
    fullNumber: string
  ): void;

  /** Callback for submitting phone number via SMS tab */
  onSubmitPhoneNumber(event?: React.SyntheticEvent<HTMLFormElement>): void;

  /** Callback for submitting SMS verification code via SMS tab */
  onSubmitSmsCode(event?: React.SyntheticEvent<HTMLFormElement>): void;

  /** Callback for submitting TOTP verification code via TOTP tab */
  onSubmitTotpCode(event?: React.SyntheticEvent<HTMLFormElement>): void;

  /** Callback to handle requesting a new SMS verification code */
  onResendSmsCode(): void;

  /** Callback to signal that MFA set is complete */
  onComplete(): void;
}

export type MfaType = 'sms' | 'totp';



// WEBPACK FOOTER //
// ./src/components/SetupMfa.tsx